import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import TiptapEditor from "../../components/TiptapEditor";
import { ArrowLeft, Save, Upload, X, Loader2, Eye, Image as ImageIcon, Trash2 } from "lucide-react";

export default function AdminArticleEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "",
    tags: [],
    featured_image: "",
    status: "draft",
  });
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const { data } = await api.get(`/admin/articles/${id}`);
          setForm({
            title: data.title,
            slug: data.slug,
            excerpt: data.excerpt,
            content: data.content,
            category: data.category,
            tags: data.tags || [],
            featured_image: data.featured_image || "",
            status: data.status,
          });
        } catch (e) {
          toast.error("No se pudo cargar el artículo");
          navigate("/admin/articulos");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, isNew, navigate]);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      setForm({ ...form, tags: [...form.tags, t] });
    }
    setTagInput("");
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, featured_image: data.url }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingCover(false);
    }
  };

  const save = async (overrideStatus) => {
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    if (!form.excerpt.trim() || form.excerpt.trim().length < 10) return toast.error("El extracto debe tener al menos 10 caracteres");
    if (!form.content || form.content.length < 20) return toast.error("El contenido es muy corto");
    if (!form.category.trim()) return toast.error("La categoría es obligatoria");

    const payload = { ...form, status: overrideStatus || form.status };
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/articles", payload);
        toast.success(`Artículo ${data.status === "published" ? "publicado" : "guardado"}`);
        navigate(`/admin/articulos/${data.id}/editar`, { replace: true });
      } else {
        const { data } = await api.put(`/admin/articles/${id}`, payload);
        setForm((prev) => ({ ...prev, status: data.status, slug: data.slug }));
        toast.success(`Artículo ${data.status === "published" ? "publicado" : "guardado"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar este artículo permanentemente?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Eliminado");
      navigate("/admin/articulos");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-cyan-600" /></div>;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/articulos" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-700 text-sm">
          <ArrowLeft size={16} /> Volver a artículos
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && form.status === "published" && (
            <a href={`/articulos/${form.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium">
              <Eye size={16} /> Ver público
            </a>
          )}
          {!isNew && (
            <button onClick={handleDelete} className="inline-flex items-center gap-2 px-4 py-2 border border-rose-200 text-rose-600 rounded-xl hover:bg-rose-50 text-sm font-medium">
              <Trash2 size={16} /> Eliminar
            </button>
          )}
          <button onClick={() => save("draft")} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar borrador
          </button>
          <button onClick={() => save("published")} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-xl shadow-md hover:shadow-lg text-sm font-semibold disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {form.status === "published" ? "Actualizar" : "Publicar"}
          </button>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-slate-900">{isNew ? "Nuevo artículo" : "Editar artículo"}</h1>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Título *</label>
              <input
                value={form.title}
                onChange={handle("title")}
                placeholder="Título del artículo"
                className="w-full px-4 py-3 text-lg font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Slug (URL) <span className="text-xs text-slate-400">— se autogenera si lo dejas vacío</span>
              </label>
              <input
                value={form.slug}
                onChange={handle("slug")}
                placeholder="ej: depresion-y-ansiedad"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Extracto / resumen *</label>
              <textarea
                value={form.excerpt}
                onChange={handle("excerpt")}
                rows={2}
                placeholder="Breve descripción que aparecerá en listados (10-400 caracteres)"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              />
              <div className="text-xs text-slate-400 mt-1 text-right">{form.excerpt.length}/400</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Contenido del artículo</h3>
              <p className="text-xs text-slate-500 mt-0.5">Usa la barra de herramientas para dar formato. Puedes insertar imágenes.</p>
            </div>
            <div className="p-4">
              <TiptapEditor value={form.content} onChange={(html) => setForm((p) => ({ ...p, content: html }))} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-3">Imagen destacada</h3>
            {form.featured_image ? (
              <div className="relative group rounded-xl overflow-hidden mb-3">
                <img src={form.featured_image} alt="" className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, featured_image: "" })}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-xl h-40 flex flex-col items-center justify-center text-slate-400 mb-3">
                <ImageIcon size={28} />
                <span className="text-xs mt-1">Sin imagen</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingCover}
              className="w-full inline-flex items-center justify-center gap-2 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium disabled:opacity-60"
            >
              {uploadingCover ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Subir imagen
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ""; }} />
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1">o pega URL</label>
              <input
                value={form.featured_image}
                onChange={handle("featured_image")}
                placeholder="https://..."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-3">Categoría *</h3>
            <input
              value={form.category}
              onChange={handle("category")}
              placeholder="Ej: Depresión"
              list="cat-options"
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
            />
            <datalist id="cat-options">
              <option value="Depresión" />
              <option value="Ansiedad" />
              <option value="Esquizofrenia" />
              <option value="TDAH" />
              <option value="Neurociencia" />
              <option value="Demencias" />
              <option value="Adicciones" />
              <option value="Trastornos Afectivos" />
              <option value="Ética médica" />
              <option value="Psicosis" />
            </datalist>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-3">Tags</h3>
            <div className="flex gap-1.5 flex-wrap mb-2">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-cyan-50 text-cyan-800 px-2.5 py-1 rounded-full text-xs">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-rose-600"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Agregar tag"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">+</button>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-3">Estado</h3>
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, status: "draft" })} className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition ${form.status === "draft" ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                Borrador
              </button>
              <button type="button" onClick={() => setForm({ ...form, status: "published" })} className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition ${form.status === "published" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                Publicado
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
